// Story 10.1/10.5 — Top bar con breadcrumbs + slot de acciones + user menu.
// Story 10.5: logout limpia toda la cache (no solo `me`) y muestra toast;
// el botón "Nuevo bot" se condiciona a `edit_bot`.
// Story 16.5: el botón deshabilitado "Cuenta (Fase 2)" pasa a ser
// "Organizaciones" (la propia si el usuario tiene una sola).

import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Building2, Menu, Plus, User } from 'lucide-react';
import { toast } from 'sonner';
import { Can } from '@/components/auth/Can';
import { Button } from '@/components/ui/button';
import { useMe } from '@/hooks/queries/use-me';
import { api } from '@/lib/api-client';
import { useMobileNavStore } from '@/stores/ui-store';
import { Breadcrumbs } from './Breadcrumbs';

export function TopBar(): React.ReactElement {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobileNavOpen = useMobileNavStore((s) => s.open);
  const setMobileNav = useMobileNavStore((s) => s.setOpen);

  // Un member con una sola organización va directo a ella; el staff y quien
  // tiene varias, a la lista.
  const onlyOrganization =
    me?.role === 'member' && me.organizations.length === 1 ? me.organizations[0] : undefined;
  async function openOrganizations(): Promise<void> {
    if (onlyOrganization) {
      await navigate({
        to: '/organizations/$organizationId',
        params: { organizationId: String(onlyOrganization.id) },
      });
      return;
    }
    await navigate({ to: '/organizations' });
  }

  async function handleLogout(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Ignore — la cookie ya puede haber expirado.
    }
    // P/Story 10.5: limpiar TODA la cache, no solo `auth/me` — datos
    // sensibles como conversaciones, analytics y stats no deben quedar
    // accesibles para el siguiente user que loguee en este browser.
    qc.clear();
    toast.success('Sesión cerrada');
    await navigate({ to: '/login' });
  }

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card/50 px-2 backdrop-blur sm:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 md:hidden"
        onClick={() => setMobileNav(!mobileNavOpen)}
        aria-label="Abrir menú"
        aria-controls="mobile-nav"
        aria-expanded={mobileNavOpen}
      >
        <Menu className="h-5 w-5" />
      </Button>
      <div className="min-w-0 flex-1">
        <Breadcrumbs />
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <Can capability="edit_bot">
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              // Navegamos al dashboard con `?new=1` — el DashboardPage
              // abre el wizard automaticamente cuando ve ese param.
              // Sin onClick el boton era cosmetico y no hacia nada.
              void navigate({ to: '/', search: { new: 1 } as never });
            }}
          >
            <Plus className="h-4 w-4" />
            <span className="sr-only sm:not-sr-only">Nuevo bot</span>
          </Button>
        </Can>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void openOrganizations()}
          title={onlyOrganization ? onlyOrganization.name : 'Organizaciones'}
        >
          <Building2 className="h-4 w-4" />
          <span className="sr-only lg:not-sr-only lg:max-w-48 lg:truncate">
            {onlyOrganization ? onlyOrganization.name : 'Organizaciones'}
          </span>
        </Button>
        <div className="flex items-center gap-1 rounded-md border border-border px-1 py-1 text-sm sm:ml-2 sm:gap-2 sm:px-2">
          {/* Story 16.6 — la cuenta: contraseña y API keys. */}
          <Link
            to="/settings/account"
            className="flex min-w-0 items-center gap-2 rounded px-1 text-muted-foreground hover:text-foreground hover:underline"
            title={me?.email ? `Tu cuenta (${me.email})` : 'Tu cuenta'}
          >
            <User className="h-4 w-4 shrink-0" aria-hidden />
            <span className="sr-only md:not-sr-only md:max-w-56 md:truncate">
              {me?.email ?? '—'}
            </span>
          </Link>
          <Button size="sm" variant="ghost" onClick={handleLogout} aria-label="Cerrar sesión">
            Salir
          </Button>
        </div>
      </div>
    </header>
  );
}
